import useGuardedRealtimeList, { readListRows } from '../../realtime/useGuardedRealtimeList';
import { resourceForUrl } from '../../realtime/resources';
import { fetchBackgroundData } from '../../redux/api/portalApi';
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react"; // Add useRef
import { useSelector, useDispatch } from "react-redux"; // Add useDispatch
import { selectApiUrls } from "../../redux/slices/apiUrlSlice";
import useHandleGlobalSearch from "../../functions/useHandleGlobalSearch"; // Import the custom hook
import TopNav from "../TopNav/TopNav";
import JobNav from "../JobDetailPage/JobNav";
import * as XLSX from "xlsx"; // Import XLSX from xlsx
import "jspdf-autotable"; // Import jspdf-autotable
import { fetchData } from "../../redux/slices/apiCallSlice"; // Import fetchData thunk
import Modal from "react-bootstrap/Modal";
import InfoBubbleModal from "../common/InfoBubbleModal";
import { getProjectId, idsMatch } from "../../utils/dataNormalization";

const AdvancedFilterRow = ({ filter, index, allFields, getUniqueValues, onUpdate, onRemove }) => {
  const [fieldSuggestions, setFieldSuggestions] = useState([]);
  const [valueSuggestions, setValueSuggestions] = useState([]);
  const [showFieldSuggestions, setShowFieldSuggestions] = useState(false);
  const [showValueSuggestions, setShowValueSuggestions] = useState(false);

  useEffect(() => {
    if (filter.fieldSearch) {
      const search = filter.fieldSearch.toLowerCase();
      setFieldSuggestions(allFields.filter((field) => field.toLowerCase().includes(search)));
    } else {
      setFieldSuggestions(allFields);
    }
  }, [filter.fieldSearch, allFields]);

  useEffect(() => {
    if (filter.field) {
      const uniqueValues = getUniqueValues(filter.field);
      if (filter.value) {
        const search = filter.value.toLowerCase();
        setValueSuggestions(uniqueValues.filter((val) => val.toLowerCase().includes(search)));
      } else {
        setValueSuggestions(uniqueValues);
      }
    } else {
      setValueSuggestions([]);
    }
  }, [filter.field, filter.value, getUniqueValues]);

  const selectField = (field) => {
    onUpdate({ field, fieldSearch: field, value: "" });
    setShowFieldSuggestions(false);
  };

  const selectValue = (value) => {
    onUpdate({ value });
    setShowValueSuggestions(false);
  };

  return (
    <div className="filter-row">
      <span className="filter-index">{index + 1}.</span>
      <div className="filter-field-selector position-relative">
        <input
          className="form-control form-control-sm"
          type="text"
          placeholder="Type to search fields..."
          value={filter.fieldSearch || filter.field}
          onChange={(e) => {
            onUpdate({ fieldSearch: e.target.value, field: "" });
            setShowFieldSuggestions(true);
          }}
          onFocus={() => setShowFieldSuggestions(true)}
          onBlur={() => setTimeout(() => setShowFieldSuggestions(false), 150)}
        />
        {showFieldSuggestions && fieldSuggestions.length > 0 && (
          <div className="suggestions-dropdown">
            {fieldSuggestions.slice(0, 10).map((field) => (
              <div key={field} className="suggestion-item" onClick={() => selectField(field)}>
                {field}
              </div>
            ))}
            {fieldSuggestions.length > 10 && (
              <div className="suggestion-item text-muted fst-italic">
                +{fieldSuggestions.length - 10} more...
              </div>
            )}
          </div>
        )}
      </div>

      <span className="filter-operator">contains</span>

      <div className="filter-value-input position-relative">
        <input
          className="form-control form-control-sm"
          type="text"
          placeholder="Enter value..."
          value={filter.value}
          onChange={(e) => {
            onUpdate({ value: e.target.value });
            setShowValueSuggestions(true);
          }}
          onFocus={() => setShowValueSuggestions(true)}
          onBlur={() => setTimeout(() => setShowValueSuggestions(false), 150)}
          disabled={!filter.field}
        />
        {showValueSuggestions && filter.field && valueSuggestions.length > 0 && (
          <div className="suggestions-dropdown">
            {valueSuggestions.slice(0, 10).map((value) => (
              <div key={value} className="suggestion-item" onClick={() => selectValue(value)}>
                {value}
              </div>
            ))}
            {valueSuggestions.length > 10 && (
              <div className="suggestion-item text-muted fst-italic">
                +{valueSuggestions.length - 10} more...
              </div>
            )}
          </div>
        )}
      </div>

      <button className="btn btn-sm btn-outline-danger" onClick={onRemove} title="Remove filter">
        <i className="bi bi-trash"></i>
      </button>
    </div>
  );
};

const GlobalSearch = () => {
  const [filter] = useState(""); // Source filter (read-only, setFilter not needed)
  const [filteredResults, setFilteredResults] = useState([]); // Add state for filtered results
  const [currentPage, setCurrentPage] = useState(1); // Add state for current page
  const [resultsPerPage, setResultsPerPage] = useState(10); // Modify state for results per page
  const [searchExecuted, setSearchExecuted] = useState(false); // Add state to track if search was executed
  const [apiLoadingStatus, setApiLoadingStatus] = useState({}); // Add local state for API loading status
  // eslint-disable-next-line no-unused-vars
  const [totalResultsCount, setTotalResultsCount] = useState(0); // Add state for total results count
  const [localData, setLocalData] = useState([]); // Add state to store all fetched data locally
  const [dataFetched, setDataFetched] = useState(false); // Add state to track if data has been fetched
  const [inputValue, setInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");  
  const [showStatusModal, setShowStatusModal] = useState(false); // Modal state
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [nextFilterId, setNextFilterId] = useState(1);
  const [expandedJobs, setExpandedJobs] = useState(new Set()); // Track expanded job details
  const [showApiSelection, setShowApiSelection] = useState(false); // Track API selection visibility
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoModalValue, setInfoModalValue] = useState("");
  const apiUrls = useSelector(selectApiUrls);
  const jobId = useSelector((state) => state.jobId.jobId);
  const dispatch = useDispatch();

  // Fields to exclude unless they match the general project table
  const excludedFields = useMemo(() => [
    "ProjectID", "JobNumber", "ModelNumber", "State", "ProjectName",
    "SalesQuoteID", "SalesRep", "ProjectManager", "ID", "SaleQuoteID",
  ], []);

  const selectedApiUrls = {
    generalProject: apiUrls.generalProject,
    generalStation: apiUrls.generalStation,
    soldToShipTo: apiUrls.soldToShipTo,
    priceBreakdown: apiUrls.priceBreakdown,
    ownersManual: apiUrls.ownersManual,
    hcRequirements: apiUrls.hcRequirements,
    skidConstruction: apiUrls.skidConstruction,
    pumpManifoldPiping: apiUrls.pumpManifoldPiping,
    stationEnclosure: apiUrls.stationEnclosure,
    dropPipeComments: apiUrls.dropPipeComments,
    dropPipes: apiUrls.dropPipes,
    levelControls: apiUrls.levelControls,
    fillSources: apiUrls.fillSources,
    pumps: apiUrls.pumps,
    pumpDetails: apiUrls.pumpDetails,
    waterTreatment: apiUrls.waterTreatment,
    inletScreen: apiUrls.inletScreen,
    inletStrainer: apiUrls.inletStrainer,
    dischargeFilter: apiUrls.dischargeFilter,
    dischargeStrainer: apiUrls.dischargeStrainer,
    roSystem: apiUrls.roSystem,
    uvSystem: apiUrls.uvSystem,
    fertigation: apiUrls.fertigation,
    skyHarvester: apiUrls.skyHarvester,
    shipLoose: apiUrls.shipLoose,
    unregulatedPower: apiUrls.unregulatedPower,
    unregulatedBreakers: apiUrls.unregulatedBreakers,
    regulatedPower: apiUrls.regulatedPower,
    regulatedBreakers: apiUrls.regulatedBreakers,
    sequenceOfOperations: apiUrls.sequenceOfOperations,
    electricalOptions: apiUrls.electricalOptions,
    telemetry: apiUrls.telemetry,
    telemetryAccessories: apiUrls.telemetryAccessories,
    controlLogic: apiUrls.controlLogicOnly, // Add controlLogic to match GenerateReport.js
  };

  const [includedApis, setIncludedApis] = useState(
    Object.keys(selectedApiUrls).reduce(
      (acc, key) => ({ ...acc, [key]: true }), // Include all APIs by default
      {}
    )
  );

  const allFilterableFields = useMemo(() => {
    const fields = new Set();
    localData.forEach((item) => {
      Object.keys(item || {}).forEach((key) => {
        if (key !== "source") {
          fields.add(key);
        }
      });
    });
    return Array.from(fields).sort();
  }, [localData]);

  const getUniqueValuesForField = useCallback(
    (fieldName) => {
      const values = localData
        .map((item) => item[fieldName])
        .filter((val) => val !== null && val !== undefined && val !== "")
        .map((val) => val.toString());
      return [...new Set(values)].sort();
    },
    [localData]
  );

  const addFilter = () => {
    setAdvancedFilters((prev) => [...prev, { id: nextFilterId, field: "", value: "", fieldSearch: "" }]);
    setNextFilterId((prev) => prev + 1);
  };

  const removeFilter = (id) => {
    setAdvancedFilters((prev) => prev.filter((f) => f.id !== id));
  };

  const updateFilter = (id, updates) => {
    setAdvancedFilters((prev) => prev.map((f) => (f.id === id ? { ...f, ...updates } : f)));
  };

  const clearAllFilters = () => {
    setAdvancedFilters([]);
    setSearchTerm("");
  };

  const toggleExpandJob = (jobKey) => {
    const newExpanded = new Set(expandedJobs);
    if (newExpanded.has(jobKey)) {
      newExpanded.delete(jobKey);
    } else {
      newExpanded.add(jobKey);
    }
    setExpandedJobs(newExpanded);
  };

  const handleOpenInfoModal = (value) => {
    setInfoModalValue(value);
    setShowInfoModal(true);
  };

  const handleCloseInfoModal = () => {
    setShowInfoModal(false);
  };

  // Get unique matching jobs with their matched fields
  const getUniqueJobsWithMatches = useCallback(
    (results) => {
      const jobMap = new Map();
      const activeAdvanced = advancedFilters.filter((f) => f.field && f.value);
      const hasAdvanced = activeAdvanced.length > 0;
      const hasSearch = searchTerm.trim() !== "";

      results.forEach((result) => {
        const jobKey = result.ProjectID || result.JobNumber;
        if (!jobMap.has(jobKey)) {
          jobMap.set(jobKey, {
            ProjectID: result.ProjectID,
            JobNumber: result.JobNumber,
            ProjectName: result.ProjectName,
            ModelNumber: result.ModelNumber,
            SalesRep: result.SalesRep,
            ProjectManager: result.ProjectManager,
            matchedFields: [],
            filterHits: new Set(),
            searchHit: false,
          });
        }

        Object.entries(result).forEach(([key, value]) => {
          const isAdvancedField = activeAdvanced.some((f) => f.field === key);
          if (value === null || value === undefined || value === "") {
            return;
          }

          const valString = value.toString();
          const valLower = valString.toLowerCase();
          const matchesSearch = hasSearch && valLower.includes(searchTerm.toLowerCase());

          let matchedAdvanced = false;
          activeAdvanced.forEach((f, idx) => {
            if (f.field === key && valLower.includes(f.value.toLowerCase())) {
              matchedAdvanced = true;
              jobMap.get(jobKey).filterHits.add(idx);
            }
          });

          // Skip fields only if excluded AND not matching search AND not matching any advanced filter
          if (excludedFields.includes(key) && !isAdvancedField && !matchesSearch && !matchedAdvanced) {
            return;
          }

          // Only collect fields that actually match search or any advanced filter
          if (matchesSearch || matchedAdvanced || (!hasAdvanced && !hasSearch)) {
            const existing = jobMap.get(jobKey).matchedFields.find((mf) => mf.field === key);
            if (!existing) {
              jobMap.get(jobKey).matchedFields.push({
                field: key,
                value: valString,
                source: result.source,
              });
            }
            if (matchesSearch) {
              jobMap.get(jobKey).searchHit = true;
            }
          }
        });
      });

      return Array.from(jobMap.values()).filter((job) => {
        if (job.matchedFields.length === 0) return false;
        const advancedOk = !hasAdvanced || job.filterHits.size === activeAdvanced.length;
        const searchOk = !hasSearch || job.searchHit;
        return advancedOk && searchOk;
      }).map((job) => ({ ...job, filterHits: undefined }));
    },
    [advancedFilters, excludedFields, searchTerm]
  );

  const applyFilters = useCallback(
    (data) => {
      const searchLower = searchTerm.toLowerCase();
      const filterLower = filter.toLowerCase();
      const activeAdvanced = advancedFilters.filter((f) => f.field && f.value);
      const hasAdvanced = activeAdvanced.length > 0;
      const hasSearch = searchTerm.trim() !== "";

      return data.filter((item) => {
        const sourceMatches = filter === "" || (item.source?.toLowerCase() || "").includes(filterLower);
        const apiIncluded = includedApis[item.source];
        if (!sourceMatches || !apiIncluded) return false;

        const matchesSearchTerm = hasSearch
          ? JSON.stringify(item).toLowerCase().includes(searchLower)
          : true;

        const matchesAnyAdvanced = hasAdvanced
          ? activeAdvanced.some((f) => {
              const itemValue = item[f.field];
              if (itemValue === null || itemValue === undefined) return false;
              return itemValue.toString().toLowerCase().includes(f.value.toLowerCase());
            })
          : true;

        // Keep items that contribute to search or any advanced filter (per-job AND enforced later)
        if (hasAdvanced || hasSearch) {
          return (hasSearch ? matchesSearchTerm : true) || (hasAdvanced ? matchesAnyAdvanced : true);
        }

        // No search and no advanced: include
        return true;
      });
    },
    [advancedFilters, filter, includedApis, searchTerm]
  );

  const { results } = useHandleGlobalSearch(
    Object.keys(selectedApiUrls).reduce((acc, key) => {
      if (includedApis[key]) {
        switch (key) {
          case "dropPipes":
            return {
              ...acc,
              dropPipes: selectedApiUrls.dropPipes,
              dropPipeComments: apiUrls.dropPipeComments,
            };
          case "levelControls":
            return {
              ...acc,
              levelControls: selectedApiUrls.levelControls,
              fillSources: apiUrls.fillSources,
            };
          case "pumps":
            return {
              ...acc,
              pumps: selectedApiUrls.pumps,
              pumpDetails: apiUrls.pumpDetails,
            };
          case "sequenceOfOperations":
            return {
              ...acc,
              sequenceOfOperations: selectedApiUrls.sequenceOfOperations,
              controlLogic: selectedApiUrls.controlLogic,
            };
          case "waterTreatment":
            return {
              ...acc,
              waterTreatment: selectedApiUrls.waterTreatment,
              inletScreen: apiUrls.inletScreen,
              inletStrainer: apiUrls.inletStrainer,
              dischargeFilter: apiUrls.dischargeFilter,
              dischargeStrainer: apiUrls.dischargeStrainer,
              roSystem: apiUrls.roSystem,
              uvSystem: apiUrls.uvSystem,
            };
          default:
            return { ...acc, [key]: selectedApiUrls[key] };
        }
      }
      return acc;
    }, {})
  );

  
  // console.log("Results:", results); // Debugging log

  const fetchDataFromApis = async ({ snapshotOnly = false, signal } = {}) => {
    const filteredApis = Object.keys(selectedApiUrls).reduce((acc, key) => {
      if (includedApis[key] || key === "generalProject") { // Always include generalProject
        switch (key) {
          case "dropPipes":
            return {
              ...acc,
              dropPipes: selectedApiUrls.dropPipes,
              dropPipeComments: apiUrls.dropPipeComments,
            };
          case "unregulatedPower":
            return {
              ...acc,
              unregulatedPower: selectedApiUrls.unregulatedPower,
              unregulatedBreakers: apiUrls.unregulatedBreakers,
            };
          case "regulatedPower":
            return {
              ...acc,
              regulatedPower: selectedApiUrls.regulatedPower,
              regulatedBreakers: apiUrls.regulatedBreakers,
            };
            case "telemetry":
            return {
              ...acc,
              telemetry: selectedApiUrls.telemetry,
              telemetryAccessories: apiUrls.telemetryAccessories,
            };
          case "levelControls":
            return {
              ...acc,
              levelControls: selectedApiUrls.levelControls,
              fillSources: apiUrls.fillSources,
            };
          case "pumps":
            return {
              ...acc,
              pumps: selectedApiUrls.pumps,
              pumpDetails: apiUrls.pumpDetails,
            };
          case "sequenceOfOperations":
            return {
              ...acc,
              sequenceOfOperations: selectedApiUrls.sequenceOfOperations,
              controlLogic: selectedApiUrls.controlLogic,
            };
          case "waterTreatment":
            return {
              ...acc,
              waterTreatment: selectedApiUrls.waterTreatment,
              inletScreen: apiUrls.inletScreen,
              inletStrainer: apiUrls.inletStrainer,
              dischargeFilter: apiUrls.dischargeFilter,
              dischargeStrainer: apiUrls.dischargeStrainer,
              roSystem: apiUrls.roSystem,
              uvSystem: apiUrls.uvSystem,
            };
          default:
            return { ...acc, [key]: selectedApiUrls[key] };
        }
      }
      return acc;
    }, {});
  
    if (!snapshotOnly) { setApiLoadingStatus({}); setDataFetched(false); setShowStatusModal(true); } // Show modal
  
    try {
      if (!snapshotOnly) setApiLoadingStatus(
        Object.keys(filteredApis).reduce((acc, key) => ({ ...acc, [key]: "loading" }), {})
      );
  
      const allResults = [];
      let generalProjectData = [];
  
      await Promise.all(
        Object.entries(filteredApis).map(async ([apiKey, url]) => {
          try {
            const response = snapshotOnly ? readListRows(await dispatch(fetchBackgroundData({ url, signal }))) : await dispatch(fetchData({ url })).unwrap();
  
            if (apiKey === "generalProject") {
              generalProjectData = response;
            }
  
            allResults.push(
              ...response.map((item) => ({ ...item, source: apiKey }))
            );
  
            if (!snapshotOnly) setApiLoadingStatus((prevStatus) => ({
              ...prevStatus,
              [apiKey]: "success",
            }));
          } catch (error) {
      if (snapshotOnly) throw error;
            if (snapshotOnly) throw error;
            console.error(`API: ${apiKey}, Error: ${error}`);
            if (!snapshotOnly) setApiLoadingStatus((prevStatus) => ({
              ...prevStatus,
              [apiKey]: "error",
            }));
          }
        })
      );
  
      const matchedResults = allResults.map((result) => {
        const matchingProject = generalProjectData.find(
          (project) => idsMatch(getProjectId(project), getProjectId(result)) && !project.HideFromList // Exclude hidden projects
        );
        return {
          ...result,
          JobNumber: matchingProject?.JobNumber || "N/A",
          ProjectName: matchingProject?.ProjectName || "N/A",
          ModelNumber: matchingProject?.ModelNumber || "N/A",
          SalesQuoteID: matchingProject?.SaleQuoteID || "N/A",
          SalesRep: matchingProject?.SalesRep || "N/A",
          ProjectManager: matchingProject?.ProjectManager || "N/A",
        };
      }).filter((result) => result.JobNumber !== "N/A" && result.ProjectName !== "N/A"); // Filter out unmatched results
  
      if (snapshotOnly) return matchedResults;
      setLocalData(matchedResults); // Store all results locally
      setDataFetched(true); // Mark data as fetched
      setShowStatusModal(false); // Hide modal
      // console.log("Fetched Data:", matchedResults);
    } catch (error) {
      if (snapshotOnly) throw error;
      console.error("Failed to fetch data:", error);
      if (!snapshotOnly) setApiLoadingStatus(
        Object.keys(filteredApis).reduce((acc, key) => ({ ...acc, [key]: "error" }), {})
      );
      setShowStatusModal(false); // Hide modal
    }
  };
  
  const searchResources = Object.values(apiUrls).flatMap(url => {
    try { const resource = resourceForUrl(url, new URL('.', apiUrls.generalProjectInfoEnhanced).href); return resource ? [resource] : []; } catch { return []; }
  });
  useGuardedRealtimeList({ apiKey: 'generalProject', enabled: dataFetched, blocked: showStatusModal,
    scopeKey: [apiUrls, includedApis], additionalResources: searchResources,
    load: ({ signal }) => fetchDataFromApis({ snapshotOnly: true, signal }), apply: setLocalData,
  });

  useEffect(() => {
    if (searchExecuted && filteredResults.length > 0) {
      // console.log("Filtered Results Ready:", filteredResults); // Debugging log
    }
  }, [searchExecuted, filteredResults]);
  

  // Use effect to update filteredResults when results change
  useEffect(() => {
    if (results) {
      const filteredResults = applyFilters(results);
      setFilteredResults(filteredResults);
      setSearchExecuted(true);
      const totalRows = filteredResults
        .flatMap((result) =>
          Object.entries(result).filter(
            ([, value]) =>
              value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
          )
        )
        .length;
      setTotalResultsCount(totalRows);
    }
  }, [applyFilters, results, searchTerm, excludedFields]);

  // Add dropdown states
  const [jobNumberFilter, setJobNumberFilter] = useState("");
  const [projectNameFilter, setProjectNameFilter] = useState("");
  const [tableFilter, setTableFilter] = useState("");
  const [fieldFilter, setFieldFilter] = useState("");

  // Update dropdown options based on filtered results
  const [jobNumberOptions, setJobNumberOptions] = useState([]);
  const [projectNameOptions, setProjectNameOptions] = useState([]);
  const [tableOptions, setTableOptions] = useState([]);

  const fieldOptions = [...new Set(
    filteredResults.flatMap((item) =>
      Object.entries(item)
        .filter(([key, value]) =>
          (searchTerm === "" ? true : value?.toString().toLowerCase().includes(searchTerm.toLowerCase())) &&
          key !== "source" && key !== "ProjectID" && key !== "JobNumber" && key !== "ProjectName"
        )
        .map(([key]) => key)
    )
  )];

  // Filter results for pagination based on excluded fields and dropdown selections
  const filteredByDropdowns = filteredResults.filter((item) => {
    const jobNumberMatch = jobNumberFilter === "" || item.JobNumber === jobNumberFilter;
    const projectNameMatch = projectNameFilter === "" || item.ProjectName === projectNameFilter;
    const tableMatch = tableFilter === "" || item.source === tableFilter;
    const fieldMatch =
      fieldFilter === "" ||
      Object.entries(item).some(([key, value]) => key === fieldFilter && value?.toString().toLowerCase().includes(searchTerm.toLowerCase()));

    return jobNumberMatch && projectNameMatch && tableMatch && fieldMatch;
  });

  // Get unique matching jobs with their matched fields
  const uniqueJobsWithMatches = useMemo(() => {
    return getUniqueJobsWithMatches(filteredByDropdowns);
  }, [filteredByDropdowns, getUniqueJobsWithMatches]);

  // Paginate unique jobs instead of individual field rows
  const indexOfLastJob = currentPage * resultsPerPage;
  const indexOfFirstJob = indexOfLastJob - resultsPerPage;
  const paginatedJobs = uniqueJobsWithMatches.slice(indexOfFirstJob, indexOfLastJob);
  const totalJobPages = Math.ceil(uniqueJobsWithMatches.length / resultsPerPage);

  // Change page
  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalJobPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleCheckboxChange = (key) => {
    setIncludedApis((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSelectAll = () => {
    setIncludedApis(Object.keys(selectedApiUrls).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
  };

  const handleDeselectAll = () => {
    setIncludedApis(Object.keys(selectedApiUrls).reduce((acc, key) => ({ ...acc, [key]: false }), {}));
  };

  const formatApiName = (name) =>
    name.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());

  const formatFieldName = (name) =>
    name.replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2").replace(/([a-z\d])([A-Z])/g, "$1 $2").replace(/^./, (str) => str.toUpperCase());

  // Highlight matching text in results
  const highlightText = (text, searchTerms) => {
    if (!searchTerms || searchTerms.length === 0) return text;
    const str = text?.toString() || "";
    let highlighted = str;
    const allTerms = [...searchTerms].sort((a, b) => b.length - a.length); // Sort by length, longest first
    
    allTerms.forEach((term) => {
      if (term) {
        const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
        highlighted = highlighted.replace(regex, '<mark class="search-highlight">$1</mark>');
      }
    });
    return highlighted;
  };

  // Get all search terms (from search input and advanced filters)
  const allSearchTerms = useMemo(() => {
    const terms = new Set();
    if (searchTerm) terms.add(searchTerm);
    advancedFilters.forEach((f) => {
      if (f.value) terms.add(f.value);
    });
    return Array.from(terms);
  }, [searchTerm, advancedFilters]);

  const handleResultsPerPageChange = (e) => {
    setResultsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when results per page changes
  };

  const csvData = filteredResults.flatMap((result) =>
    Object.entries(result)
      .filter(([, value]) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
      .map(([key, value]) => ({
        ProjectID: result.ProjectID || "N/A",
         SalesQuoteID: result.SalesQuoteID || "N/A",
        JobNumber: result.JobNumber || "N/A",
        ModelNumber: result.ModelNumber || "N/A",
        ProjectName: result.ProjectName || "N/A",
        ProjectManager: result.ProjectManager || "N/A",
        SalesRep: result.SalesRep || "N/A",
        source: result.source,
        field: formatFieldName(key),
        value: value !== null ? value.toString() : "N/A",
        
       
       
        
      }))
  );

  const exportToXLS = () => {
    const worksheet = XLSX.utils.json_to_sheet(csvData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Results");
    XLSX.writeFile(workbook, "search_results.xlsx");
  };

  const groupedApis = {
    general: {
      generalProject: selectedApiUrls.generalProject,
      generalStation: selectedApiUrls.generalStation,
    },
    sales: {
      soldToShipTo: selectedApiUrls.soldToShipTo,
      priceBreakdown: selectedApiUrls.priceBreakdown,
    },
    mechanical: {
      hcRequirements: selectedApiUrls.hcRequirements,
      skidConstruction: selectedApiUrls.skidConstruction,
      pumpManifoldPiping: selectedApiUrls.pumpManifoldPiping,
      stationEnclosure: selectedApiUrls.stationEnclosure,
      fertigation: selectedApiUrls.fertigation,
      dropPipes: selectedApiUrls.dropPipes,
      levelControls: selectedApiUrls.levelControls,
      pumps: selectedApiUrls.pumps,
      skyHarvester: selectedApiUrls.skyHarvester,
      shipLoose: selectedApiUrls.shipLoose,
    },
    electrical: {
      electricalOptions: selectedApiUrls.electricalOptions,
      unregulatedPower: selectedApiUrls.unregulatedPower,
      regulatedPower: selectedApiUrls.regulatedPower,
      telemetry: selectedApiUrls.telemetry,
      sequenceOfOperations: selectedApiUrls.sequenceOfOperations,
      controlLogic: selectedApiUrls.controlLogic,
    },
    shared: {
      waterTreatment: selectedApiUrls.waterTreatment, // Group waterTreatment and related APIs
    },
  };

  const isLoading = Object.values(apiLoadingStatus).some((status) => status === "loading");
  const isSuccess = Object.values(apiLoadingStatus).every((status) => status === "success");

  // Remove the debounce logic and directly update searchTerm
  const handleSearchInputChange = (e) => {
    setInputValue(e.target.value);
    setSearchTerm(e.target.value); // Update searchTerm immediately
  };

  // UseEffect to trigger search when searchTerm changes
  useEffect(() => {
    if (dataFetched) {
      const filtered = applyFilters(localData);
      setFilteredResults(filtered);
      const totalRows = filtered
        .flatMap((result) =>
          Object.entries(result).filter(
            ([key, value]) =>
              value?.toString().toLowerCase().includes(searchTerm.toLowerCase()) &&
              (result.source === "generalProject" || !excludedFields.includes(key))
          )
        )
        .length;
      setTotalResultsCount(totalRows);
      setSearchExecuted(true);
    }
  }, [applyFilters, dataFetched, excludedFields, localData, searchTerm]);

  // Reset to page 1 only when search term or filters change
  const prevFiltersRef = useRef();
  useEffect(() => {
    const currentFilters = JSON.stringify({
      searchTerm,
      advancedFilters,
      jobNumberFilter,
      projectNameFilter,
      tableFilter,
      fieldFilter
    });
    
    if (prevFiltersRef.current && prevFiltersRef.current !== currentFilters) {
      setCurrentPage(1);
    }
    prevFiltersRef.current = currentFilters;
  }, [searchTerm, advancedFilters, jobNumberFilter, projectNameFilter, tableFilter, fieldFilter]);

  useEffect(() => {
    // Update dropdown options dynamically based on uniqueJobsWithMatches
    const updatedJobNumberOptions = [...new Set(uniqueJobsWithMatches.map((item) => item.JobNumber || "N/A"))];
    const updatedProjectNameOptions = [...new Set(uniqueJobsWithMatches.map((item) => item.ProjectName || "N/A"))];
    const updatedTableOptions = []; // Table filter not needed for unique jobs view

    setJobNumberOptions(updatedJobNumberOptions);
    setProjectNameOptions(updatedProjectNameOptions);
    setTableOptions(updatedTableOptions);
  }, [uniqueJobsWithMatches, fieldFilter, searchTerm]);

  return (
    <div>
      <TopNav />
      <JobNav jobId={jobId} />
      <div className="container-fluid px-4">
        <h1 className="my-4">
          Advanced Search{" "}
          {isLoading && <i className="bi bi-arrow-repeat" style={{ color: "blue" }}></i>} {/* Spinner icon */}
          {isSuccess && <i className="bi bi-check-circle" style={{ color: "green" }}></i>} {/* Success icon */}
        </h1>
        <div className="mb-3">
          <button className="btn btn-primary" onClick={fetchDataFromApis}>
            Fetch Data
          </button>
        </div>
        {/* API Selection Checkboxes */}
        <div className="mb-3">
          <button
            className="btn btn-outline-secondary w-100 text-start"
            onClick={() => setShowApiSelection(!showApiSelection)}
          >
            <i className={`bi bi-${showApiSelection ? "chevron-down" : "chevron-right"} me-2`}></i>
            <strong>Select APIs to Include</strong> (Default: All)
          </button>
          {showApiSelection && (
            <div style={{ marginTop: "12px" }}>
              <div className="d-flex flex-wrap">
                {Object.entries(groupedApis).map(([section, apis]) => (
                  <div key={section} className="mb-3">
                    <h6>{section.charAt(0).toUpperCase() + section.slice(1)}</h6>
                    {Object.keys(apis).map((key) => (
                      <div key={key} className="form-check me-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={key}
                          checked={includedApis[key]}
                          onChange={() => handleCheckboxChange(key)}
                        />
                        <label className="form-check-label" htmlFor={key}>
                          {formatApiName(key)}
                        </label>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div className="mt-2">
                <button className="btn btn-primary me-2" onClick={handleSelectAll}>
                  Select All
                </button>
                <button className="btn btn-secondary" onClick={handleDeselectAll}>
                  Deselect All
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Search Input */}
        <div className="input-group mb-3" style={{ maxWidth: "600px" }}>
          <span className="input-group-text">Search</span>
          <input
            type="text"
            className="form-control"
            placeholder="Search by value..."
            value={inputValue}
            onChange={handleSearchInputChange}
          />
          <button
            className="btn btn-outline-info"
            type="button"
            onClick={() => handleOpenInfoModal("Global Search")}
          >
            <i className="bi bi-info-circle"></i>
          </button>
        </div>

        {/* Advanced Filters */}
        <div className="mb-3 advanced-filters-card">
          <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
            <h5 className="mb-0 d-flex align-items-center gap-2">
              <i className="bi bi-funnel"></i>
              Advanced Filters
            </h5>
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-info"
                type="button"
                onClick={() => handleOpenInfoModal("Global Advanced Filters")}
              >
                <i className="bi bi-info-circle"></i>
              </button>
              {(advancedFilters.length > 0 || searchTerm) && (
                <button className="btn btn-sm btn-outline-secondary" onClick={clearAllFilters}>
                  <i className="bi bi-x-circle me-1"></i>
                  Clear All
                </button>
              )}
              <button className="btn btn-sm btn-success" onClick={addFilter}>
                <i className="bi bi-plus-circle me-1"></i>
                Add Filter
              </button>
            </div>
          </div>
          {advancedFilters.length > 0 && (
            <div className="filters-list">
              {advancedFilters.map((filter, index) => (
                <AdvancedFilterRow
                  key={filter.id}
                  filter={filter}
                  index={index}
                  allFields={allFilterableFields}
                  getUniqueValues={getUniqueValuesForField}
                  onUpdate={(updates) => updateFilter(filter.id, updates)}
                  onRemove={() => removeFilter(filter.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Dropdown Filters */}
        <div className="mb-3">
          <h5>Filter Results</h5>
          <div className="row">
            <div className="col-md-3">
              <label htmlFor="jobNumberFilter" className="form-label">Job Number</label>
              <select
                id="jobNumberFilter"
                className="form-select"
                value={jobNumberFilter}
                onChange={(e) => setJobNumberFilter(e.target.value)}
              >
                <option value="">All</option>
                {jobNumberOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label htmlFor="projectNameFilter" className="form-label">Project Name</label>
              <select
                id="projectNameFilter"
                className="form-select"
                value={projectNameFilter}
                onChange={(e) => setProjectNameFilter(e.target.value)}
              >
                <option value="">All</option>
                {projectNameOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label htmlFor="tableFilter" className="form-label">Table</label>
              <select
                id="tableFilter"
                className="form-select"
                value={tableFilter}
                onChange={(e) => setTableFilter(e.target.value)}
              >
                <option value="">All</option>
                {tableOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label htmlFor="fieldFilter" className="form-label">Field</label>
              <select
                id="fieldFilter"
                className="form-select"
                value={fieldFilter}
                onChange={(e) => setFieldFilter(e.target.value)}
              >
                <option value="">All</option>
                {fieldOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Show number of results */}
        {searchExecuted && (
          <div className="my-3 d-flex justify-content-between align-items-center">
            <small>{`Jobs Found: ${uniqueJobsWithMatches.length}`}</small>
            <div className="d-flex align-items-center">
              <button className="btn btn-secondary me-2" onClick={exportToXLS}>
                Export as XLS
              </button>
              <label htmlFor="resultsPerPage" className="form-label me-2 mb-0">Jobs Per Page:</label>
              <select
                id="resultsPerPage"
                className="form-select form-select-sm"
                value={resultsPerPage}
                onChange={handleResultsPerPageChange}
                style={{ width: "auto" }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="text-center my-4">
            <div class="spinner-border" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <div class="mt-3">
              <h5>API Statuses:</h5>
              <ul>
                {Object.entries(apiLoadingStatus).map(([api, status]) => (
                  <p key={api}>
                    {api}: {status}
                  </p>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          searchExecuted && (
            <>
              {uniqueJobsWithMatches.length > 0 ? (
                <>
                  <div style={{ overflowX: "auto" }}>
                    <table
                      className="table table-bordered table-striped"
                      style={{ width: "100%", minWidth: "1200px", marginBottom: "0" }}
                    >
                      <thead>
                        <tr>
                          <th style={{ width: "50px" }}></th>
                          <th>Project ID</th>
                          <th>Job Number</th>
                          <th>Project Name</th>
                          <th>Model Number</th>
                          <th>Sales Rep</th>
                          <th style={{ width: "150px" }}>Matching Fields</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedJobs && paginatedJobs.map((job, index) => {
                          const jobKey = job.ProjectID || job.JobNumber;
                          const isExpanded = expandedJobs.has(jobKey);

                          return (
                            <React.Fragment key={jobKey}>
                              <tr style={{ backgroundColor: "#f8f9fa", fontWeight: "500" }}>
                                <td style={{ textAlign: "center", cursor: "pointer" }} onClick={() => toggleExpandJob(jobKey)}>
                                  <i className={`bi bi-${isExpanded ? "chevron-down" : "chevron-right"}`}></i>
                                </td>
                                <td>{job.ProjectID || "N/A"}</td>
                                <td>{job.JobNumber}</td>
                                <td>{job.ProjectName}</td>
                                <td>{job.ModelNumber || "N/A"}</td>
                                <td>{job.SalesRep || "N/A"}</td>
                                <td>
                                  <span className="badge bg-info">{job.matchedFields.length} fields</span>
                                </td>
                              </tr>
                              {isExpanded && job.matchedFields.length > 0 && (
                                <tr>
                                  <td colSpan="7" style={{ backgroundColor: "#f0f0f0", padding: "15px" }}>
                                    <table className="table table-sm table-borderless mb-0">
                                      <tbody>
                                        {job.matchedFields.map((match, idx) => (
                                          <tr key={idx}>
                                            <td style={{ width: "200px", fontWeight: "500" }}>{formatFieldName(match.field)}</td>
                                            <td>
                                              <span dangerouslySetInnerHTML={{ __html: highlightText(match.value, allSearchTerms) }} />
                                            </td>
                                            <td style={{ width: "120px", fontSize: "0.85rem", color: "#666" }}>
                                              <em>{match.source}</em>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 d-flex justify-content-between align-items-center">
                    <div className="text-muted">
                      Showing {paginatedJobs.length > 0 ? indexOfFirstJob + 1 : 0} - {Math.min(indexOfLastJob, uniqueJobsWithMatches.length)} of {uniqueJobsWithMatches.length} jobs
                    </div>
                    {/* Pagination */}
                    {totalJobPages > 1 && (
                      <nav className="pagination-wrapper">
                        <ul className="pagination mb-0">
                          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                            <button
                              onClick={() => paginate(currentPage - 1)}
                              className="page-link"
                              disabled={currentPage === 1}
                            >
                              Previous
                            </button>
                          </li>
                          {Array.from({ length: totalJobPages }, (_, i) => (
                            <li key={i + 1} className={`page-item ${currentPage === i + 1 ? "active" : ""}`}>
                              <button onClick={() => paginate(i + 1)} className="page-link">
                                {i + 1}
                              </button>
                            </li>
                          ))}
                          <li className={`page-item ${currentPage === totalJobPages ? "disabled" : ""}`}>
                            <button
                              onClick={() => paginate(currentPage + 1)}
                              className="page-link"
                              disabled={currentPage === totalJobPages}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    )}
                  </div>
                </>
              ) : (
                <div className="alert alert-info">No jobs found. Try adjusting your search or filters.</div>
              )}
            </>
          )
        )}
      </div>

      {/* Status Modal */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>API Fetch Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ul>
            {Object.entries(apiLoadingStatus).map(([api, status]) => (
              <li key={api}>
                {api}: {status === "loading" ? "Loading..." : status}
              </li>
            ))}
          </ul>
        </Modal.Body>
      </Modal>

      <InfoBubbleModal
        show={showInfoModal}
        onHide={handleCloseInfoModal}
        sectionName="GlobalSearch"
        value={infoModalValue || "Global Search"}
      />
    </div>
  );
};

// Add CSS for pagination wrapper
const styles = `
  .pagination-wrapper {
    display: flex;
    justify-content: center;
  }
  .pagination {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin: 0;
  }
  .filter-row {
    display: grid;
    grid-template-columns: auto 1fr auto 1fr auto;
    gap: 8px;
    align-items: center;
    margin-bottom: 8px;
  }
  .filter-index {
    min-width: 24px;
    text-align: right;
    color: #6c757d;
  }
  .filter-operator {
    color: #6c757d;
    font-size: 0.9rem;
  }
  .suggestions-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    max-height: 220px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    z-index: 20;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  .suggestion-item {
    padding: 6px 10px;
    cursor: pointer;
  }
  .suggestion-item:hover {
    background-color: #f8f9fa;
  }
  .advanced-filters-card {
    border: 1px solid #e0e4e8;
    border-radius: 8px;
    padding: 12px 14px;
    background: #f9fafb;
  }
  .search-highlight {
    background-color: #fff3cd;
    padding: 0;
    margin: 0;
    font-weight: inherit;
    font-kerning: auto;
  }
`;

// Inject styles into the document head
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default GlobalSearch;
